import { AdminCouponsResponse, Coupon } from '@fsp/types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaperSection from '../../../components/PaperSection';
import { apiClient } from '../../../services/apiClient';
import { formatDate, DateFormatOptions } from '@fsp/shared';
import CreateCouponDialog from './CreateCouponDialog';

export default function CouponsList() {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/admin/coupons?limit=100');

      if (!response.ok) {
        throw new Error('Failed to fetch coupons');
      }

      const data: AdminCouponsResponse = await response.json();
      setCoupons(data.coupons);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching coupons:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.percentOff) {
      return `${coupon.percentOff}% off`;
    }
    if (coupon.amountOff && coupon.currency) {
      return `${coupon.amountOff.toFixed(2)} ${coupon.currency.toUpperCase()} off`;
    }
    return 'N/A';
  };

  const formatDuration = (coupon: Coupon) => {
    if (coupon.duration === 'forever') {
      return 'Forever';
    }
    if (coupon.duration === 'once') {
      return 'Once';
    }
    if (coupon.duration === 'repeating' && coupon.durationInMonths) {
      return `${coupon.durationInMonths} month${coupon.durationInMonths > 1 ? 's' : ''}`;
    }
    return coupon.duration;
  };

  return (
  <>
    <PaperSection
      title="Coupons"
      action={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Coupon
        </Button>
      }
    >
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Promotion Codes</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Redeemed</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No coupons found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                coupons.map((coupon) => (
                  <TableRow
                    key={coupon.id}
                    onClick={() => navigate(`/app/coupons/${coupon.id}`)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace' }}>{coupon.id}</TableCell>
                    <TableCell>{coupon.name || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {coupon.promotionCodes.length > 0 ? (
                          coupon.promotionCodes.map((promoCode, idx) => (
                            <Chip
                              key={idx}
                              label={`${promoCode.code} (${promoCode.attributions})`}
                              size="small"
                              variant={promoCode.active ? 'filled' : 'outlined'}
                              disabled={!promoCode.active}
                              sx={{ fontFamily: 'monospace' }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No codes
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDiscount(coupon)}</TableCell>
                    <TableCell>{formatDuration(coupon)}</TableCell>
                    <TableCell>
                      {coupon.timesRedeemed}
                      {coupon.maxRedemptions ? ` / ${coupon.maxRedemptions}` : ''}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={coupon.valid ? 'Valid' : 'Invalid'}
                        color={coupon.valid ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(coupon.created, DateFormatOptions.DATEONLY)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && coupons.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Showing {coupons.length} coupon{coupons.length !== 1 ? 's' : ''}
        </Typography>
      )}
    </PaperSection>

    <CreateCouponDialog
      open={createDialogOpen}
      onClose={() => setCreateDialogOpen(false)}
      onSuccess={() => {
        setCreateDialogOpen(false);
        fetchCoupons();
      }}
    />
  </>
  );
}
